import { useQuery } from '@tanstack/react-query'
import useAuthStore from '../../../stores/useAuthStore/useAuthStore'
import useFiltersStore from '../../useFiltersStore/useFiltersStore'
import {
    Commodity,
    Contract,
    Account,
    Facility,
    Meter,
    Submeter,
    Tag,
    USNAccount,
    Provider,
} from '../types'
import { useAxiosInstance } from '../useAxiosInstance'
import {
    GetNodesRequestResponse,
    GetCommoditiesRequestParams,
    GetProvidersRequestParams,
    GetContractsRequestParams,
    GetAccountsRequestParams,
    GetFacilitiesRequestParams,
    GetMetersRequestParams,
    GetSubmetersRequestParams,
    GetTagsRequestParams,
    GetUSNsRequestParams,
    GetInvoiceNoRequestParams,
} from './types'

export const useFiltersApi = () => {
    const accessToken = useAuthStore((state) => state.accessToken)
    const axiosInstance = useAxiosInstance(accessToken)
    const customerId =
        useAuthStore((state) => state.customer?.customerId.toString()) ?? ''
    const userId = useAuthStore((state) => state.user?.id) ?? ''
    const contactId =
        useAuthStore((state) => state.customer?.contactId?.toString()) ?? ''

    const setRootNode = useFiltersStore((state) => state.setRootNode)

    const useNodesQuery = () => {
        return useQuery({
            queryKey: ['nodes', customerId, contactId],
            queryFn: async () => {
                const response =
                    await axiosInstance.get<GetNodesRequestResponse>(
                        `/api/v1/customers/${customerId}/contacts/${contactId}/nodes`
                    )

                if (response.data.nodes.length >= 1) {
                    setRootNode(response.data.nodes[0])

                    return response.data.nodes[0]
                }

                return response.data
            },
            enabled: !!accessToken && !!customerId && !!contactId,
            staleTime: 0,
        })
    }

    const useCommoditiesQuery = (
        params: Partial<GetCommoditiesRequestParams>
    ) => {
        return useQuery({
            queryKey: ['commodities', customerId, params],
            queryFn: async () => {
                const response = await axiosInstance.get<Commodity[]>(
                    `/api/v1/customers/${customerId}/commodities`,
                    {
                        params,
                    }
                )
                return response.data
            },
            enabled: !!accessToken && !!customerId && !!params['nodeIds[]'],
        })
    }

    const useProvidersQuery = (params: Partial<GetProvidersRequestParams>) => {
        return useQuery({
            queryKey: ['providers', customerId, params],
            queryFn: async () => {
                const response = await axiosInstance.get<Provider[]>(
                    `/api/v1/customers/${customerId}/providers`,
                    {
                        params,
                    }
                )
                return response.data
            },
            enabled: !!accessToken && !!customerId && !!params['nodeIds[]'],
        })
    }

    const useContractsQuery = (params: GetContractsRequestParams) => {
        return useQuery({
            queryKey: ['contracts', customerId, params],
            queryFn: async () => {
                const response = await axiosInstance.get<Contract[]>(
                    `/api/v1/customers/${customerId}/contracts`,
                    {
                        params,
                    }
                )
                return response.data
            },
            enabled: !!accessToken && !!customerId && !!params['nodeIds[]'],
        })
    }

    const useAccountsQuery = (params: GetAccountsRequestParams) => {
        return useQuery({
            queryKey: ['accounts', customerId, params],
            queryFn: async () => {
                const response = await axiosInstance.get<Account[]>(
                    `/api/v1/customers/${customerId}/pans`,
                    {
                        params,
                    }
                )
                return response.data
            },
            enabled: !!accessToken && !!customerId && !!params['nodeIds[]'],
        })
    }

    const useFacilitiesQuery = (params: GetFacilitiesRequestParams) => {
        return useQuery<Facility[]>({
            queryKey: ['facilities', customerId, params],
            queryFn: async () => {
                const response = await axiosInstance.get<Facility[]>(
                    `/api/v1/customers/${customerId}/facilities`,
                    {
                        params,
                    }
                )
                return response.data
            },
            enabled: !!accessToken && !!customerId && !!params['nodeIds[]'],
        })
    }

    const useMetersQuery = (params: GetMetersRequestParams) => {
        return useQuery({
            queryKey: ['meters', customerId, params],
            queryFn: async () => {
                const response = await axiosInstance.get<Meter[]>(
                    `/api/v1/customers/${customerId}/meters`,
                    {
                        params,
                    }
                )
                return response.data
            },
            enabled: !!accessToken && !!customerId && !!params['nodeIds[]'],
        })
    }

    const useSubmetersQuery = (params: GetSubmetersRequestParams) => {
        return useQuery({
            queryKey: ['subMeters', customerId, params],
            queryFn: async () => {
                const response = await axiosInstance.get<Submeter[]>(
                    `/api/v1/customers/${customerId}/sub-meters`,
                    {
                        params,
                    }
                )
                return response.data
            },
            enabled: !!accessToken && !!customerId && !!params['nodeIds[]'],
        })
    }

    const useTagsQuery = (params: GetTagsRequestParams) => {
        return useQuery({
            queryKey: ['tags', userId, params],
            queryFn: async () => {
                const response = await axiosInstance.get<Tag[]>(
                    `/api/v1/users/${userId}/tags`,
                    {
                        params,
                    }
                )
                return response.data
            },
            enabled: !!accessToken && !!userId && !!params['nodeIds[]'],
        })
    }

    const useUSNsQuery = (params: GetUSNsRequestParams) => {
        return useQuery<USNAccount[]>({
            queryKey: ['usns', customerId, params],
            queryFn: async () => {
                const response = await axiosInstance.get<USNAccount[]>(
                    `/api/v1/customers/${customerId}/usns`,
                    {
                        params,
                    }
                )
                return response.data
            },
            enabled: !!accessToken && !!customerId && !!params['nodeIds[]'],
        })
    }

    const useInvoiceNoQuery = (params: Partial<GetInvoiceNoRequestParams>) => {
        return useQuery({
            queryKey: ['invoiceNo', customerId, params],
            queryFn: async () => {
                const response = await axiosInstance.get<string[]>(
                    `/api/v1/customers/${customerId}/invoices/numbers`,
                    {
                        params,
                    }
                )
                return response.data
            },
            enabled: !!accessToken && !!customerId && !!params['nodeIds[]'],
        })
    }

    return {
        useNodesQuery,
        useCommoditiesQuery,
        useProvidersQuery,
        useContractsQuery,
        useAccountsQuery,
        useFacilitiesQuery,
        useTagsQuery,
        useMetersQuery,
        useSubmetersQuery,
        useUSNsQuery,
        useInvoiceNoQuery,
    }
}
